import { supabase } from '../lib/supabase';

// Helper function to check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Case conversion helpers
function toSnakeCase(str) {
  return str?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
}

function toCamelCase(str) {
  return str?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
}

function convertKeysToCamelCase(obj) {
  if (Array.isArray(obj)) {
    return obj?.map(item => convertKeysToCamelCase(item));
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const camelKey = toCamelCase(key);
      acc[camelKey] = convertKeysToCamelCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
}

function convertKeysToSnakeCase(obj) {
  if (Array.isArray(obj)) {
    return obj?.map(item => convertKeysToSnakeCase(item));
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const snakeKey = toSnakeCase(key);
      acc[snakeKey] = convertKeysToSnakeCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
}

// Get current user's role and center
export async function getCurrentUserProfile() {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError) throw authError;
    if (!user) return null;

    // Try direct table query first
    const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

    if (!error && data) {
      return convertKeysToCamelCase(data);
    }

    // If permission error, fall back to SECURITY DEFINER RPC function
    if (error?.code === '42501' || error?.message?.includes('permission denied')) {
      const { data: rpcData, error: rpcError } = await supabase?.rpc('get_current_user_profile');
      if (!rpcError && rpcData && rpcData?.length > 0) {
        return convertKeysToCamelCase(rpcData?.[0]);
      }
      // Last resort: build profile from JWT claims
      const jwtRole = user?.user_metadata?.role || user?.app_metadata?.role || 'sales';
      const jwtCenterId = user?.user_metadata?.center_id || user?.app_metadata?.center_id || null;
      return convertKeysToCamelCase({
        id: user?.id,
        email: user?.email,
        full_name: user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || '',
        role: jwtRole,
        center_id: jwtCenterId,
        is_active: true
      });
    }

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Profile fetch error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Get user profile error:', error?.message);
    throw error;
  }
}

// ==================== INVENTORY OPERATIONS ====================

export async function getInventory(filters = {}) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    let query = supabase?.from('inventory')?.select('*');

    // Role-based filtering
    if (userProfile?.role !== 'admin') {
      query = query?.eq('center_id', userProfile?.centerId);
    }

    // Apply filters
    if (filters?.centerId) {
      query = query?.eq('center_id', filters?.centerId);
    }
    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }
    if (filters?.floor) {
      query = query?.eq('floor', filters?.floor);
    }
    if (filters?.unitType) {
      query = query?.eq('unit_type', filters?.unitType);
    }

    const { data, error } = await query?.order('unit_number', { ascending: true });

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Get inventory error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Get inventory error:', error?.message);
    throw error;
  }
}

export async function createInventory(inventoryData) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    const dataToInsert = convertKeysToSnakeCase({
      ...inventoryData,
      createdBy: userProfile?.id
    });

    const { data, error } = await supabase?.from('inventory')?.insert(dataToInsert)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Create inventory error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create inventory error:', error?.message);
    throw error;
  }
}

export async function updateInventory(id, updates) {
  try {
    const dataToUpdate = convertKeysToSnakeCase(updates);

    const { data, error } = await supabase?.from('inventory')?.update(dataToUpdate)?.eq('id', id)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Update inventory error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update inventory error:', error?.message);
    throw error;
  }
}

// Fetch all inventory units with tenant information
export const fetchInventoryUnits = async (centerId = null) => {
  try {
    let query = supabase?.from('inventory')?.select(`
        *,
        tenant:tenants(id, name, company)
      `)?.order('created_at', { ascending: false });

    if (centerId) {
      query = query?.eq('center_id', centerId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(unit => ({
      id: unit?.id,
      centerId: unit?.center_id,
      unitNumber: unit?.unit_number,
      floor: unit?.floor,
      unitType: unit?.unit_type,
      coworkingSeatType: unit?.coworking_seat_type,
      capacityType: unit?.capacity_type,
      capacitySeats: unit?.capacity_seats,
      capacitySqft: unit?.capacity_sqft,
      pricingModel: unit?.pricing_model,
      pricePerSeat: unit?.price_per_seat,
      pricePerSqft: unit?.price_per_sqft,
      fixedMonthlyPrice: unit?.fixed_monthly_price,
      areaSqft: unit?.area_sqft,
      totalPrice: unit?.total_price,
      status: unit?.status,
      tenantId: unit?.tenant_id,
      tenant: unit?.tenant?.name,
      tenantCompany: unit?.tenant?.company,
      notes: unit?.notes,
      createdAt: unit?.created_at,
      updatedAt: unit?.updated_at
    })) || [];
  } catch (error) {
    console.error('Error fetching inventory units:', error);
    throw error;
  }
};

// Calculate revenue for an inventory unit based on pricing model
export function calculateInventoryRevenue(unit) {
  if (!unit || unit?.status !== 'occupied') return 0;

  switch (unit?.pricingModel) {
    case 'per_seat':
      return (unit?.capacitySeats || 0) * (unit?.pricePerSeat || 0);
    case 'per_sqft':
      return (unit?.capacitySqft || unit?.areaSqft || 0) * (unit?.pricePerSqft || 0);
    case 'fixed':
      return unit?.fixedMonthlyPrice || 0;
    default:
      return 0;
  }
}

// Get total inventory revenue for a center
export async function getInventoryRevenue(centerId = null) {
  try {
    const filters = centerId ? { centerId, status: 'occupied' } : { status: 'occupied' };
    const inventory = await getInventory(filters);
    
    return inventory?.reduce((total, unit) => {
      return total + calculateInventoryRevenue(unit);
    }, 0);
  } catch (error) {
    console.error('Get inventory revenue error:', error?.message);
    return 0;
  }
}

export async function deleteInventory(id) {
  try {
    const { error } = await supabase?.from('inventory')?.delete()?.eq('id', id);

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Delete inventory error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete inventory error:', error?.message);
    throw error;
  }
}

export async function bulkCreateInventory(inventoryArray, centerId) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    const dataToInsert = inventoryArray?.map(item => convertKeysToSnakeCase({
      ...item,
      centerId: centerId || item?.centerId,
      createdBy: userProfile?.id
    }));

    const { data, error } = await supabase?.from('inventory')?.insert(dataToInsert)?.select();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Bulk create inventory error:', error?.message);
      return { success: false, error: error?.message, data: null };
    }

    await logActivity({
      module: 'inventory',
      action: 'bulk_action',
      entityType: 'inventory',
      entityName: 'Bulk Inventory Import',
      description: `Imported ${data?.length} inventory units`
    });

    return { success: true, data: convertKeysToCamelCase(data), error: null };
  } catch (error) {
    console.error('Bulk create inventory error:', error?.message);
    return { success: false, error: error?.message, data: null };
  }
}

// ==================== LEADS OPERATIONS ====================

export async function getLeads(filters = {}) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    let query = supabase?.from('leads')?.select('*, assigned_to_profile:user_profiles!leads_assigned_to_fkey(id, full_name, email)');

    // Role-based filtering (RLS policies handle most filtering)
    // Super admin sees all, admin sees allocated centers, others see their center
    if (!['super_admin', 'admin']?.includes(userProfile?.role)) {
      query = query?.eq('center_id', userProfile?.centerId);
    }

    // Apply additional filters
    if (filters?.centerId) {
      query = query?.eq('center_id', filters?.centerId);
    }
    if (filters?.stage) {
      query = query?.eq('stage', filters?.stage);
    }
    if (filters?.assignedTo) {
      query = query?.eq('assigned_to', filters?.assignedTo);
    }
    if (filters?.search) {
      query = query?.or(`company_name.ilike.%${filters?.search}%,contact_person.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%`);
    }

    query = query?.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      if (isSchemaError(error)) throw error;
      if (error?.code === 'PGRST116') return [];
      console.log('Leads fetch error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get leads error:', error?.message);
    throw error;
  }
}

export async function createLead(leadData) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    const dataToInsert = convertKeysToSnakeCase({
      ...leadData,
      createdBy: userProfile?.id
    });

    const { data, error } = await supabase?.from('leads')?.insert(dataToInsert)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Create lead error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create lead error:', error?.message);
    throw error;
  }
}

export async function updateLead(id, updates) {
  try {
    const dataToUpdate = convertKeysToSnakeCase(updates);

    const { data, error } = await supabase?.from('leads')?.update(dataToUpdate)?.eq('id', id)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Update lead error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update lead error:', error?.message);
    throw error;
  }
}

export async function deleteLead(id) {
  try {
    const { error } = await supabase?.from('leads')?.delete()?.eq('id', id);

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Delete lead error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete lead error:', error?.message);
    throw error;
  }
}

// ==================== TENANTS OPERATIONS ====================

export async function getTenants(filters = {}) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    let query = supabase?.from('tenants')?.select('*, unit:inventory(id, unit_number, floor, area_sqft)');

    // Role-based filtering
    if (userProfile?.role !== 'admin') {
      query = query?.eq('center_id', userProfile?.centerId);
    }

    // Apply additional filters
    if (filters?.centerId) {
      query = query?.eq('center_id', filters?.centerId);
    }
    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }
    if (filters?.riskLevel) {
      query = query?.eq('risk_level', filters?.riskLevel);
    }
    if (filters?.search) {
      query = query?.or(`company_name.ilike.%${filters?.search}%,contact_person.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%`);
    }

    query = query?.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      if (isSchemaError(error)) throw error;
      if (error?.code === 'PGRST116') return [];
      console.log('Tenants fetch error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get tenants error:', error?.message);
    throw error;
  }
}

export async function createTenant(tenantData) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    const dataToInsert = convertKeysToSnakeCase({
      ...tenantData,
      createdBy: userProfile?.id
    });

    const { data, error } = await supabase?.from('tenants')?.insert(dataToInsert)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Create tenant error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create tenant error:', error?.message);
    throw error;
  }
}

export async function updateTenant(id, updates) {
  try {
    const dataToUpdate = convertKeysToSnakeCase(updates);

    const { data, error } = await supabase?.from('tenants')?.update(dataToUpdate)?.eq('id', id)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Update tenant error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update tenant error:', error?.message);
    throw error;
  }
}

export async function deleteTenant(id) {
  try {
    const { error } = await supabase?.from('tenants')?.delete()?.eq('id', id);

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Delete tenant error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete tenant error:', error?.message);
    throw error;
  }
}

// ==================== MANDATES OPERATIONS ====================

export async function getMandates(filters = {}) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    let query = supabase?.from('mandates')?.select('*');

    // Role-based filtering
    if (userProfile?.role !== 'admin') {
      query = query?.eq('center_id', userProfile?.centerId);
    }

    // Apply additional filters
    if (filters?.centerId) {
      query = query?.eq('center_id', filters?.centerId);
    }
    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }
    if (filters?.search) {
      query = query?.or(`property_unit_number.ilike.%${filters?.search}%,notes.ilike.%${filters?.search}%`);
    }

    query = query?.order('mandate_expiry_date', { ascending: true });

    const { data, error } = await query;

    if (error) {
      if (isSchemaError(error)) throw error;
      if (error?.code === 'PGRST116') return [];
      console.log('Mandates fetch error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get mandates error:', error?.message);
    throw error;
  }
}

export async function createMandate(mandateData) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    const dataToInsert = convertKeysToSnakeCase({
      ...mandateData,
      createdBy: userProfile?.id
    });

    const { data, error } = await supabase?.from('mandates')?.insert(dataToInsert)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Create mandate error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create mandate error:', error?.message);
    throw error;
  }
}

export async function updateMandate(mandateId, mandateData) {
  try {
    const dataToUpdate = convertKeysToSnakeCase(mandateData);

    const { data, error } = await supabase?.from('mandates')?.update(dataToUpdate)?.eq('id', mandateId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Update mandate error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data?.[0]);
  } catch (error) {
    console.error('Update mandate error:', error?.message);
    throw error;
  }
}

export async function bulkUpdateMandates(mandateIds, mandateData) {
  try {
    const dataToUpdate = convertKeysToSnakeCase(mandateData);

    const { data, error } = await supabase?.from('mandates')?.update(dataToUpdate)?.in('id', mandateIds)?.select();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Bulk update mandates error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Bulk update mandates error:', error?.message);
    throw error;
  }
}

export async function deleteMandate(mandateId) {
  try {
    const { error } = await supabase?.from('mandates')?.delete()?.eq('id', mandateId);

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Delete mandate error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete mandate error:', error?.message);
    throw error;
  }
}

export function subscribeToMandates(callback) {
  const channel = supabase?.channel('mandates_changes')?.on(
    'postgres_changes',
    { event: '*', schema: 'public', table: 'mandates' },
    () => {
      if (callback) callback();
    }
  )?.subscribe();

  return channel;
}

// ==================== REAL-TIME SUBSCRIPTIONS ====================

/**
 * Subscribe to inventory table changes
 * @param {Function} callback - Function to call when data changes (receives payload)
 * @param {Object} filter - Optional filter (e.g., { centerId: 'uuid' })
 * @returns {Object} channel - Supabase channel object (use for cleanup)
 */
export function subscribeToInventory(callback, filter = {}) {
  try {
    const channelName = `inventory_changes_${Date.now()}`;
    let subscriptionConfig = {
      event: '*',
      schema: 'public',
      table: 'inventory'
    };

    // Add filter if provided
    if (filter?.centerId) {
      subscriptionConfig.filter = `center_id=eq.${filter?.centerId}`;
    }

    const channel = supabase?.channel(channelName)?.on('postgres_changes', subscriptionConfig, (payload) => {
        callback(payload);
      })?.subscribe();

    return channel;
  } catch (error) {
    console.error('Subscribe to inventory error:', error?.message);
    return null;
  }
}

/**
 * Subscribe to leads table changes
 * @param {Function} callback - Function to call when data changes (receives payload)
 * @param {Object} filter - Optional filter (e.g., { centerId: 'uuid' })
 * @returns {Object} channel - Supabase channel object (use for cleanup)
 */
export function subscribeToLeads(callback, filter = {}) {
  try {
    const channelName = `leads_changes_${Date.now()}`;
    let subscriptionConfig = {
      event: '*',
      schema: 'public',
      table: 'leads'
    };

    // Add filter if provided
    if (filter?.centerId) {
      subscriptionConfig.filter = `center_id=eq.${filter?.centerId}`;
    }

    const channel = supabase?.channel(channelName)?.on('postgres_changes', subscriptionConfig, (payload) => {
        callback(payload);
      })?.subscribe();

    return channel;
  } catch (error) {
    console.error('Subscribe to leads error:', error?.message);
    return null;
  }
}

/**
 * Subscribe to tenants table changes
 * @param {Function} callback - Function to call when data changes (receives payload)
 * @param {Object} filter - Optional filter (e.g., { centerId: 'uuid' })
 * @returns {Object} channel - Supabase channel object (use for cleanup)
 */
export function subscribeToTenants(callback, filter = {}) {
  try {
    const channelName = `tenants_changes_${Date.now()}`;
    let subscriptionConfig = {
      event: '*',
      schema: 'public',
      table: 'tenants'
    };

    // Add filter if provided
    if (filter?.centerId) {
      subscriptionConfig.filter = `center_id=eq.${filter?.centerId}`;
    }

    const channel = supabase?.channel(channelName)?.on('postgres_changes', subscriptionConfig, (payload) => {
        callback(payload);
      })?.subscribe();

    return channel;
  } catch (error) {
    console.error('Subscribe to tenants error:', error?.message);
    return null;
  }
}

/**
 * Unsubscribe from a channel
 * @param {Object} channel - Supabase channel object
 */
export function unsubscribeChannel(channel) {
  if (channel) {
    supabase?.removeChannel(channel);
  }
}

// ==================== ACTIVITY LOGS OPERATIONS ====================

/**
 * Log user activity to activity_logs table
 * @param {Object} activityData - Activity log data
 * @param {string} activityData.module - Module name (dashboard, inventory, leads, tenants, mandates)
 * @param {string} activityData.action - Action type (create, update, delete, status_change, assignment, note_added, bulk_action, export, import)
 * @param {string} activityData.entityType - Entity type (inventory, lead, tenant, mandate, etc.)
 * @param {string} activityData.entityId - Entity UUID (optional)
 * @param {string} activityData.entityName - Entity name/identifier
 * @param {string} activityData.description - Human-readable description
 * @param {Object} activityData.oldValue - Previous value (optional, for updates)
 * @param {Object} activityData.newValue - New value (optional, for updates)
 * @returns {Object} Created activity log
 */
export async function logActivity(activityData) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) {
      console.log('Cannot log activity: User not authenticated');
      return null;
    }

    const dataToInsert = convertKeysToSnakeCase({
      userId: userProfile?.id,
      userName: userProfile?.fullName || userProfile?.email,
      userRole: userProfile?.role,
      centerId: userProfile?.centerId,
      module: activityData?.module,
      action: activityData?.action,
      entityType: activityData?.entityType,
      entityId: activityData?.entityId || null,
      entityName: activityData?.entityName,
      description: activityData?.description,
      oldValue: activityData?.oldValue ? JSON.stringify(activityData?.oldValue) : null,
      newValue: activityData?.newValue ? JSON.stringify(activityData?.newValue) : null
    });

    const { data, error } = await supabase?.from('activity_logs')?.insert(dataToInsert)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Log activity error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Log activity error:', error?.message);
    // Don't throw error to prevent activity logging from breaking main operations
    return null;
  }
}

/**
 * Get activity logs with filters
 * @param {Object} filters - Filter options
 * @param {string} filters.module - Filter by module
 * @param {string} filters.action - Filter by action type
 * @param {string} filters.userId - Filter by user ID
 * @param {string} filters.entityType - Filter by entity type
 * @param {string} filters.entityId - Filter by entity ID
 * @param {string} filters.startDate - Filter by start date
 * @param {string} filters.endDate - Filter by end date
 * @param {string} filters.search - Search in description and entity name
 * @param {number} filters.limit - Limit results (default: 100)
 * @returns {Array} Activity logs
 */
export async function getActivityLogs(filters = {}) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    let query = supabase?.from('activity_logs')?.select('*');

    // Role-based filtering
    if (userProfile?.role !== 'admin') {
      query = query?.eq('center_id', userProfile?.centerId);
    }

    // Apply additional filters
    if (filters?.module) {
      query = query?.eq('module', filters?.module);
    }
    if (filters?.action) {
      query = query?.eq('action', filters?.action);
    }
    if (filters?.userId) {
      query = query?.eq('user_id', filters?.userId);
    }
    if (filters?.entityType) {
      query = query?.eq('entity_type', filters?.entityType);
    }
    if (filters?.entityId) {
      query = query?.eq('entity_id', filters?.entityId);
    }
    if (filters?.startDate) {
      query = query?.gte('created_at', filters?.startDate);
    }
    if (filters?.endDate) {
      query = query?.lte('created_at', filters?.endDate);
    }
    if (filters?.search) {
      query = query?.or(`description.ilike.%${filters?.search}%,entity_name.ilike.%${filters?.search}%`);
    }

    // Order by most recent first
    query = query?.order('created_at', { ascending: false });

    // Apply limit
    const limit = filters?.limit || 100;
    query = query?.limit(limit);

    const { data, error } = await query;

    if (error) {
      if (isSchemaError(error)) throw error;
      if (error?.code === 'PGRST116') return [];
      console.log('Activity logs fetch error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get activity logs error:', error?.message);
    throw error;
  }
}

/**
 * Get activity logs statistics
 * @returns {Object} Activity statistics
 */
export async function getActivityStats() {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    let query = supabase?.from('activity_logs')?.select('module, action, created_at');

    // Role-based filtering
    if (userProfile?.role !== 'admin') {
      query = query?.eq('center_id', userProfile?.centerId);
    }

    const { data, error } = await query;

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Activity stats fetch error:', error?.message);
      return null;
    }

    // Calculate statistics
    const totalActivities = data?.length || 0;
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    
    const todayActivities = data?.filter(log => {
      const logDate = new Date(log?.created_at);
      return logDate >= today;
    })?.length || 0;

    const moduleStats = {};
    const actionStats = {};

    data?.forEach(log => {
      moduleStats[log?.module] = (moduleStats?.[log?.module] || 0) + 1;
      actionStats[log?.action] = (actionStats?.[log?.action] || 0) + 1;
    });

    return {
      total: totalActivities,
      today: todayActivities,
      byModule: moduleStats,
      byAction: actionStats
    };
  } catch (error) {
    console.error('Get activity stats error:', error?.message);
    throw error;
  }
}

/**
 * Subscribe to activity logs changes
 * @param {Function} callback - Function to call when data changes
 * @returns {Object} channel - Supabase channel object
 */
export function subscribeToActivityLogs(callback) {
  try {
    const channelName = `activity_logs_changes_${Date.now()}`;
    const channel = supabase?.channel(channelName)?.on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'activity_logs' },
      (payload) => {
        callback(payload);
      }
    )?.subscribe();

    return channel;
  } catch (error) {
    console.error('Subscribe to activity logs error:', error?.message);
    return null;
  }
}

// ==================== DASHBOARD STATISTICS ====================

export async function getDashboardStats() {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    // Get inventory stats
    let inventoryQuery = supabase?.from('inventory')?.select('status, total_price');
    
    if (userProfile?.role !== 'admin') {
      inventoryQuery = inventoryQuery?.eq('center_id', userProfile?.centerId);
    }

    const { data: inventoryData, error: inventoryError } = await inventoryQuery;

    if (inventoryError && isSchemaError(inventoryError)) throw inventoryError;

    // Get leads stats
    let leadsQuery = supabase?.from('leads')?.select('stage, budget_per_sqft, required_area_sqft');
    
    if (userProfile?.role !== 'admin') {
      leadsQuery = leadsQuery?.eq('center_id', userProfile?.centerId);
    }

    const { data: leadsData, error: leadsError } = await leadsQuery;

    if (leadsError && isSchemaError(leadsError)) throw leadsError;

    // Get tenants stats
    let tenantsQuery = supabase?.from('tenants')?.select('status, monthly_rent, lease_end_date');
    
    if (userProfile?.role !== 'admin') {
      tenantsQuery = tenantsQuery?.eq('center_id', userProfile?.centerId);
    }

    const { data: tenantsData, error: tenantsError } = await tenantsQuery;

    if (tenantsError && isSchemaError(tenantsError)) throw tenantsError;

    // Calculate stats
    const totalInventory = inventoryData?.length || 0;
    const vacantUnits = inventoryData?.filter(i => i?.status === 'vacant')?.length || 0;
    const occupiedUnits = inventoryData?.filter(i => i?.status === 'occupied')?.length || 0;
    const totalValue = inventoryData?.reduce((sum, i) => sum + (parseFloat(i?.total_price) || 0), 0) || 0;

    const totalLeads = leadsData?.length || 0;
    const activeLeads = leadsData?.filter(l => !['won', 'lost']?.includes(l?.stage))?.length || 0;
    const wonLeads = leadsData?.filter(l => l?.stage === 'won')?.length || 0;

    const totalTenants = tenantsData?.length || 0;
    const activeTenants = tenantsData?.filter(t => t?.status === 'active')?.length || 0;
    const monthlyRevenue = tenantsData?.reduce((sum, t) => sum + (parseFloat(t?.monthly_rent) || 0), 0) || 0;

    // Get expiring lock-ins (next 30 days)
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow?.setDate(thirtyDaysFromNow?.getDate() + 30);
    
    let expiringQuery = supabase?.from('inventory')?.select('*')?.not('lock_in_expiry_date', 'is', null)?.lte('lock_in_expiry_date', thirtyDaysFromNow?.toISOString()?.split('T')?.[0])?.gte('lock_in_expiry_date', new Date()?.toISOString()?.split('T')?.[0]);
    
    if (userProfile?.role !== 'admin') {
      expiringQuery = expiringQuery?.eq('center_id', userProfile?.centerId);
    }

    const { data: expiringData, error: expiringError } = await expiringQuery?.order('lock_in_expiry_date', { ascending: true });

    if (expiringError && isSchemaError(expiringError)) throw expiringError;

    return {
      inventory: {
        total: totalInventory,
        vacant: vacantUnits,
        occupied: occupiedUnits,
        totalValue: totalValue
      },
      leads: {
        total: totalLeads,
        active: activeLeads,
        won: wonLeads
      },
      tenants: {
        total: totalTenants,
        active: activeTenants,
        monthlyRevenue: monthlyRevenue
      },
      expiringLockIns: convertKeysToCamelCase(expiringData || [])
    };
  } catch (error) {
    console.error('Get dashboard stats error:', error?.message);
    throw error;
  }
}

// ==================== REPORTS MODULE ====================

export async function getRevenueByCenter() {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    let inventoryQuery = supabase?.from('inventory')?.select('center_id, pricing_model, capacity_seats, capacity_sqft, area_sqft, price_per_seat, price_per_sqft, fixed_monthly_price, status, created_at');
    let tenantsQuery = supabase?.from('tenants')?.select('center_id, monthly_rent, lease_start_date, status');

    if (userProfile?.role !== 'admin') {
      inventoryQuery = inventoryQuery?.eq('center_id', userProfile?.centerId);
      tenantsQuery = tenantsQuery?.eq('center_id', userProfile?.centerId);
    }

    const { data: inventoryData, error: invError } = await inventoryQuery;
    const { data: tenantsData, error: tenError } = await tenantsQuery;

    if (invError || tenError) {
      console.log('Revenue query error:', invError || tenError);
      return [];
    }

    const inventoryDataCamel = convertKeysToCamelCase(inventoryData || []);
    const tenantsDataCamel = convertKeysToCamelCase(tenantsData || []);

    const centerRevenue = {};

    // Calculate inventory revenue using hybrid pricing model
    inventoryDataCamel?.forEach(item => {
      if (!centerRevenue?.[item?.centerId]) {
        centerRevenue[item?.centerId] = { inventoryRevenue: 0, tenantRevenue: 0 };
      }
      if (item?.status === 'occupied') {
        centerRevenue[item?.centerId].inventoryRevenue += calculateInventoryRevenue(item);
      }
    });

    // Calculate tenant revenue
    tenantsDataCamel?.forEach(tenant => {
      if (!centerRevenue?.[tenant?.centerId]) {
        centerRevenue[tenant?.centerId] = { inventoryRevenue: 0, tenantRevenue: 0 };
      }
      if (tenant?.status === 'active') {
        centerRevenue[tenant?.centerId].tenantRevenue += tenant?.monthlyRent || 0;
      }
    });

    return Object?.entries(centerRevenue)?.map(([centerId, revenue]) => ({
      centerId,
      inventoryRevenue: revenue?.inventoryRevenue,
      tenantRevenue: revenue?.tenantRevenue,
      totalRevenue: revenue?.inventoryRevenue + revenue?.tenantRevenue
    }));
  } catch (error) {
    console.error('Get revenue by center error:', error?.message);
    throw error;
  }
}

export async function getOccupancyTrend(months = 6) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    let query = supabase?.from('inventory')?.select('status, created_at, updated_at');

    if (userProfile?.role !== 'admin') {
      query = query?.eq('center_id', userProfile?.centerId);
    }

    const { data, error } = await query;

    if (error && isSchemaError(error)) throw error;

    const trendData = [];
    const today = new Date();

    for (let i = months - 1; i >= 0; i--) {
      const monthDate = new Date(today?.getFullYear(), today?.getMonth() - i, 1);
      const monthName = monthDate?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      
      const total = data?.length || 0;
      const occupied = data?.filter(item => item?.status === 'occupied')?.length || 0;
      const vacant = data?.filter(item => item?.status === 'vacant')?.length || 0;
      const occupancyRate = total > 0 ? (occupied / total) * 100 : 0;

      trendData?.push({
        month: monthName,
        occupancyRate: parseFloat(occupancyRate?.toFixed(2)),
        occupied,
        vacant,
        total
      });
    }

    return trendData;
  } catch (error) {
    console.error('Get occupancy trend error:', error?.message);
    throw error;
  }
}

export async function getLeadSourcePerformance() {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    let query = supabase?.from('leads')?.select('source, stage, budget_per_sqft, required_area_sqft');

    if (userProfile?.role !== 'admin') {
      query = query?.eq('center_id', userProfile?.centerId);
    }

    const { data, error } = await query;

    if (error && isSchemaError(error)) throw error;

    const sourceStats = {};

    data?.forEach(lead => {
      const source = lead?.source || 'Unknown';
      if (!sourceStats?.[source]) {
        sourceStats[source] = { total: 0, won: 0, lost: 0, active: 0, conversionRate: 0 };
      }
      sourceStats[source].total += 1;
      if (lead?.stage === 'won') sourceStats[source].won += 1;
      if (lead?.stage === 'lost') sourceStats[source].lost += 1;
      if (!['won', 'lost']?.includes(lead?.stage)) sourceStats[source].active += 1;
    });

    return Object.entries(sourceStats)?.map(([source, stats]) => ({
      source,
      total: stats?.total,
      won: stats?.won,
      lost: stats?.lost,
      active: stats?.active,
      conversionRate: stats?.total > 0 ? ((stats?.won / stats?.total) * 100)?.toFixed(2) : 0
    }));
  } catch (error) {
    console.error('Get lead source performance error:', error?.message);
    throw error;
  }
}

export async function getRenewalForecast() {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    let query = supabase?.from('tenants')?.select('*, unit:inventory(area_sqft)');

    if (userProfile?.role !== 'admin') {
      query = query?.eq('center_id', userProfile?.centerId);
    }

    const { data, error } = await query;

    if (error && isSchemaError(error)) throw error;

    const forecast = [];
    const today = new Date();

    for (let i = 0; i < 12; i++) {
      const monthDate = new Date(today?.getFullYear(), today?.getMonth() + i, 1);
      const monthEnd = new Date(today?.getFullYear(), today?.getMonth() + i + 1, 0);
      const monthName = monthDate?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

      const expiringTenants = data?.filter(tenant => {
        const leaseEnd = new Date(tenant?.lease_end_date);
        return leaseEnd >= monthDate && leaseEnd <= monthEnd;
      });

      const totalExpiring = expiringTenants?.length || 0;
      const highRisk = expiringTenants?.filter(t => t?.risk_level === 'high')?.length || 0;
      const mediumRisk = expiringTenants?.filter(t => t?.risk_level === 'medium')?.length || 0;
      const lowRisk = expiringTenants?.filter(t => t?.risk_level === 'low')?.length || 0;
      let revenueAtRisk = expiringTenants?.reduce((sum, t) => sum + (parseFloat(t?.monthly_rent) || 0), 0);

      forecast?.push({
        month: monthName,
        totalExpiring,
        highRisk,
        mediumRisk,
        lowRisk,
        revenueAtRisk
      });
    }

    return forecast;
  } catch (error) {
    console.error('Get renewal forecast error:', error?.message);
    throw error;
  }
}

export async function getVacancyHeatmap() {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    let query = supabase?.from('inventory')?.select('center_id, floor, status, area_sqft');

    if (userProfile?.role !== 'admin') {
      query = query?.eq('center_id', userProfile?.centerId);
    }

    const { data, error } = await query;

    if (error && isSchemaError(error)) throw error;

    const heatmapData = {};

    data?.forEach(item => {
      const key = `${item?.center_id}-${item?.floor}`;
      if (!heatmapData?.[key]) {
        heatmapData[key] = {
          centerId: item?.center_id,
          floor: item?.floor,
          total: 0,
          vacant: 0,
          occupied: 0,
          vacancyRate: 0
        };
      }
      heatmapData[key].total += 1;
      if (item?.status === 'vacant') heatmapData[key].vacant += 1;
      if (item?.status === 'occupied') heatmapData[key].occupied += 1;
    });

    return Object.values(heatmapData)?.map(item => ({
      ...item,
      vacancyRate: item?.total > 0 ? ((item?.vacant / item?.total) * 100)?.toFixed(2) : 0
    }));
  } catch (error) {
    console.error('Get vacancy heatmap error:', error?.message);
    throw error;
  }
}

// ==================== NOTIFICATIONS MODULE ====================

export async function getNotifications(filters = {}) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    let query = supabase?.from('notifications')?.select('*')?.eq('user_id', userProfile?.id);

    if (filters?.type) {
      query = query?.eq('type', filters?.type);
    }
    if (filters?.priority) {
      query = query?.eq('priority', filters?.priority);
    }
    if (filters?.isRead !== undefined) {
      query = query?.eq('is_read', filters?.isRead);
    }

    query = query?.order('created_at', { ascending: false })?.limit(filters?.limit || 50);

    const { data, error } = await query;

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Notifications fetch error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get notifications error:', error?.message);
    throw error;
  }
}

export async function markNotificationAsRead(notificationId) {
  try {
    const { data, error } = await supabase?.from('notifications')?.update({ is_read: true, read_at: new Date()?.toISOString() })?.eq('id', notificationId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Mark notification read error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Mark notification as read error:', error?.message);
    throw error;
  }
}

export async function markAllNotificationsAsRead() {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    const { error } = await supabase?.from('notifications')?.update({ is_read: true, read_at: new Date()?.toISOString() })?.eq('user_id', userProfile?.id)?.eq('is_read', false);

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Mark all notifications read error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Mark all notifications as read error:', error?.message);
    throw error;
  }
}

export async function deleteNotification(notificationId) {
  try {
    const { error } = await supabase?.from('notifications')?.delete()?.eq('id', notificationId);

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Delete notification error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete notification error:', error?.message);
    throw error;
  }
}

export async function getNotificationStats() {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    const { data, error } = await supabase?.from('notifications')?.select('type, priority, is_read')?.eq('user_id', userProfile?.id);

    if (error && isSchemaError(error)) throw error;

    const total = data?.length || 0;
    const unread = data?.filter(n => !n?.is_read)?.length || 0;
    const critical = data?.filter(n => n?.priority === 'critical' && !n?.is_read)?.length || 0;
    const high = data?.filter(n => n?.priority === 'high' && !n?.is_read)?.length || 0;

    return { total, unread, critical, high };
  } catch (error) {
    console.error('Get notification stats error:', error?.message);
    throw error;
  }
}

export function subscribeToNotifications(callback) {
  const channel = supabase?.channel('notifications-changes')?.on('postgres_changes', { event: '*', schema: 'public', table: 'notifications' }, callback)?.subscribe();
  return channel;
}

// ==================== SQFT REVENUE ENGINE ====================

export async function getSqftRevenueAnalytics() {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    let inventoryQuery = supabase?.from('inventory')?.select('center_id, area_sqft, price_per_sqft, total_price, status');
    let tenantsQuery = supabase?.from('tenants')?.select('center_id, monthly_rent, unit:inventory(area_sqft)');

    if (userProfile?.role !== 'admin') {
      inventoryQuery = inventoryQuery?.eq('center_id', userProfile?.centerId);
      tenantsQuery = tenantsQuery?.eq('center_id', userProfile?.centerId);
    }

    const { data: inventoryData, error: invError } = await inventoryQuery;
    const { data: tenantsData, error: tenError } = await tenantsQuery;

    if (invError && isSchemaError(invError)) throw invError;
    if (tenError && isSchemaError(tenError)) throw tenError;

    const centerAnalytics = {};

    inventoryData?.forEach(item => {
      if (!centerAnalytics?.[item?.center_id]) {
        centerAnalytics[item?.center_id] = {
          totalSqft: 0,
          occupiedSqft: 0,
          vacantSqft: 0,
          totalRevenue: 0,
          avgPricePerSqft: 0,
          revenuePerSqft: 0
        };
      }
      const sqft = parseFloat(item?.area_sqft) || 0;
      centerAnalytics[item?.center_id].totalSqft += sqft;
      if (item?.status === 'occupied') {
        centerAnalytics[item?.center_id].occupiedSqft += sqft;
      } else if (item?.status === 'vacant') {
        centerAnalytics[item?.center_id].vacantSqft += sqft;
      }
    });

    tenantsData?.forEach(tenant => {
      const centerId = tenant?.center_id;
      if (!centerAnalytics?.[centerId]) {
        centerAnalytics[centerId] = {
          totalSqft: 0,
          occupiedSqft: 0,
          vacantSqft: 0,
          totalRevenue: 0,
          avgPricePerSqft: 0,
          revenuePerSqft: 0
        };
      }
      centerAnalytics[centerId].totalRevenue += parseFloat(tenant?.monthly_rent) || 0;
    });

    return Object.entries(centerAnalytics)?.map(([centerId, data]) => {
      const revenuePerSqft = data?.occupiedSqft > 0 ? data?.totalRevenue / data?.occupiedSqft : 0;
      const utilizationRate = data?.totalSqft > 0 ? (data?.occupiedSqft / data?.totalSqft) * 100 : 0;
      
      return {
        centerId,
        totalSqft: data?.totalSqft,
        occupiedSqft: data?.occupiedSqft,
        vacantSqft: data?.vacantSqft,
        totalRevenue: data?.totalRevenue,
        revenuePerSqft: parseFloat(revenuePerSqft?.toFixed(2)),
        utilizationRate: parseFloat(utilizationRate?.toFixed(2)),
        potentialRevenue: data?.vacantSqft * revenuePerSqft
      };
    });
  } catch (error) {
    console.error('Get sqft revenue analytics error:', error?.message);
    throw error;
  }
}

export async function getSqftOptimizationInsights() {
  try {
    const analytics = await getSqftRevenueAnalytics();
    
    const insights = analytics?.map(center => {
      const insights = [];
      
      if (center?.utilizationRate < 70) {
        insights?.push({
          type: 'warning',
          message: `Low utilization (${center?.utilizationRate}%). Consider marketing vacant spaces.`,
          impact: 'high'
        });
      }
      
      if (center?.vacantSqft > 0 && center?.revenuePerSqft > 0) {
        insights?.push({
          type: 'opportunity',
          message: `Potential revenue of ₹${center?.potentialRevenue?.toFixed(0)} from ${center?.vacantSqft} sqft vacant space.`,
          impact: 'medium'
        });
      }
      
      if (center?.revenuePerSqft > 0) {
        const avgMarket = 50;
        const variance = ((center?.revenuePerSqft - avgMarket) / avgMarket) * 100;
        if (variance < -10) {
          insights?.push({
            type: 'alert',
            message: `Revenue per sqft is ${Math.abs(variance)?.toFixed(1)}% below market average.`,
            impact: 'high'
          });
        }
      }
      
      return {
        centerId: center?.centerId,
        insights
      };
    });
    
    return insights;
  } catch (error) {
    console.error('Get sqft optimization insights error:', error?.message);
    throw error;
  }
}

// ==================== VACANCY PREDICTION AI ====================

export async function predictOccupancyDrop() {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    let tenantsQuery = supabase?.from('tenants')?.select('center_id, lease_end_date, risk_level, payment_history_score, monthly_rent, unit:inventory(area_sqft)');

    if (userProfile?.role !== 'admin') {
      tenantsQuery = tenantsQuery?.eq('center_id', userProfile?.centerId);
    }

    const { data: tenantsData, error: tenError } = await tenantsQuery;

    if (tenError && isSchemaError(tenError)) throw tenError;

    const predictions = [];
    const today = new Date();

    for (let i = 1; i <= 6; i++) {
      const monthDate = new Date(today?.getFullYear(), today?.getMonth() + i, 1);
      const monthEnd = new Date(today?.getFullYear(), today?.getMonth() + i + 1, 0);
      const monthName = monthDate?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

      const expiringTenants = tenantsData?.filter(tenant => {
        const leaseEnd = new Date(tenant?.lease_end_date);
        return leaseEnd >= monthDate && leaseEnd <= monthEnd;
      });

      let predictedChurn = 0;
      let revenueAtRisk = 0;
      let sqftAtRisk = 0;

      expiringTenants?.forEach(tenant => {
        let churnProbability = 0;
        
        if (tenant?.risk_level === 'high') churnProbability = 0.7;
        else if (tenant?.risk_level === 'medium') churnProbability = 0.4;
        else churnProbability = 0.15;
        
        if (tenant?.payment_history_score < 70) churnProbability += 0.2;
        else if (tenant?.payment_history_score < 85) churnProbability += 0.1;
        
        churnProbability = Math.min(churnProbability, 0.95);
        
        predictedChurn += churnProbability;
        revenueAtRisk += (parseFloat(tenant?.monthly_rent) || 0) * churnProbability;
        sqftAtRisk += (parseFloat(tenant?.unit?.area_sqft) || 0) * churnProbability;
      });

      predictions?.push({
        month: monthName,
        expiringCount: expiringTenants?.length,
        predictedChurn: Math.round(predictedChurn),
        revenueAtRisk: parseFloat(revenueAtRisk?.toFixed(2)),
        sqftAtRisk: parseFloat(sqftAtRisk?.toFixed(2)),
        churnRate: expiringTenants?.length > 0 ? ((predictedChurn / expiringTenants?.length) * 100)?.toFixed(2) : 0
      });
    }

    return predictions;
  } catch (error) {
    console.error('Predict occupancy drop error:', error?.message);
    throw error;
  }
}

export async function calculateRenewalChurnProbability() {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    let query = supabase?.from('tenants')?.select('*, unit:inventory(area_sqft, price_per_sqft)');

    if (userProfile?.role !== 'admin') {
      query = query?.eq('center_id', userProfile?.centerId);
    }

    const { data, error } = await query;

    if (error && isSchemaError(error)) throw error;

    const today = new Date();
    const ninetyDaysFromNow = new Date(today?.getTime() + 90 * 24 * 60 * 60 * 1000);

    const tenantsAtRisk = data?.filter(tenant => {
      const leaseEnd = new Date(tenant?.lease_end_date);
      return leaseEnd <= ninetyDaysFromNow && leaseEnd >= today;
    })?.map(tenant => {
      let churnScore = 0;
      let factors = [];
      
      if (tenant?.risk_level === 'high') churnScore += 35;
      else if (tenant?.risk_level === 'medium') churnScore += 20;
      
      if (tenant?.payment_history_score < 70) {
        churnScore += 30;
        factors?.push('Poor payment history');
      } else if (tenant?.payment_history_score < 85) {
        churnScore += 15;
        factors?.push('Below average payment history');
      }
      
      const leaseEnd = new Date(tenant?.lease_end_date);
      const daysUntilExpiry = Math.ceil((leaseEnd - today) / (1000 * 60 * 60 * 24));
      if (daysUntilExpiry <= 30) {
        churnScore += 20;
        factors?.push('Lease expiring within 30 days');
      } else if (daysUntilExpiry <= 60) {
        churnScore += 10;
        factors?.push('Lease expiring within 60 days');
      }
      
      const monthlyRent = parseFloat(tenant?.monthly_rent) || 0;
      const unitSqft = parseFloat(tenant?.unit?.area_sqft) || 1;
      const currentRate = monthlyRent / unitSqft;
      const marketRate = parseFloat(tenant?.unit?.price_per_sqft) || currentRate;
      
      if (currentRate < marketRate * 0.8) {
        churnScore += 15;
        factors?.push('Below market rate (likely to renew)');
      } else if (currentRate > marketRate * 1.2) {
        churnScore += 25;
        factors?.push('Above market rate (price sensitive)');
      }
      
      churnScore = Math.min(churnScore, 100);
      
      let churnCategory = 'Low';
      if (churnScore >= 70) churnCategory = 'Critical';
      else if (churnScore >= 50) churnCategory = 'High';
      else if (churnScore >= 30) churnCategory = 'Medium';
      
      return {
        ...convertKeysToCamelCase(tenant),
        churnProbability: churnScore,
        churnCategory,
        riskFactors: factors,
        daysUntilExpiry
      };
    });

    return tenantsAtRisk?.sort((a, b) => b?.churnProbability - a?.churnProbability);
  } catch (error) {
    console.error('Calculate renewal churn probability error:', error?.message);
    throw error;
  }
}

export async function generateDynamicPricingSuggestions() {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    let query = supabase?.from('inventory')?.select('*');

    if (userProfile?.role !== 'admin') {
      query = query?.eq('center_id', userProfile?.centerId);
    }

    const { data, error } = await query;

    if (error && isSchemaError(error)) throw error;

    const centerStats = {};
    data?.forEach(item => {
      if (!centerStats?.[item?.center_id]) {
        centerStats[item?.center_id] = {
          prices: [],
          occupiedCount: 0,
          vacantCount: 0,
          totalCount: 0
        };
      }
      centerStats?.[item?.center_id]?.prices?.push(parseFloat(item?.price_per_sqft) || 0);
      centerStats[item?.center_id].totalCount += 1;
      if (item?.status === 'occupied') centerStats[item?.center_id].occupiedCount += 1;
      if (item?.status === 'vacant') centerStats[item?.center_id].vacantCount += 1;
    });

    const suggestions = data?.filter(item => item?.status === 'vacant')?.map(item => {
      const centerData = centerStats?.[item?.center_id];
      const avgPrice = centerData?.prices?.reduce((a, b) => a + b, 0) / centerData?.prices?.length;
      const occupancyRate = (centerData?.occupiedCount / centerData?.totalCount) * 100;
      
      const currentPrice = parseFloat(item?.price_per_sqft) || 0;
      let suggestedPrice = currentPrice;
      let reason = '';
      let action = 'maintain';
      
      if (occupancyRate < 60) {
        suggestedPrice = currentPrice * 0.9;
        reason = 'Low occupancy rate - reduce price to attract tenants';
        action = 'decrease';
      } else if (occupancyRate > 85) {
        suggestedPrice = currentPrice * 1.1;
        reason = 'High occupancy rate - increase price to maximize revenue';
        action = 'increase';
      } else if (currentPrice > avgPrice * 1.15) {
        suggestedPrice = avgPrice * 1.05;
        reason = 'Price significantly above market average';
        action = 'decrease';
      } else if (currentPrice < avgPrice * 0.85) {
        suggestedPrice = avgPrice * 0.95;
        reason = 'Price below market average - opportunity to increase';
        action = 'increase';
      } else {
        reason = 'Price is competitive with market';
        action = 'maintain';
      }
      
      const priceChange = suggestedPrice - currentPrice;
      const priceChangePercent = ((priceChange / currentPrice) * 100)?.toFixed(2);
      
      return {
        ...convertKeysToCamelCase(item),
        currentPrice,
        suggestedPrice: parseFloat(suggestedPrice?.toFixed(2)),
        priceChange: parseFloat(priceChange?.toFixed(2)),
        priceChangePercent: parseFloat(priceChangePercent),
        action,
        reason,
        marketAverage: parseFloat(avgPrice?.toFixed(2)),
        occupancyRate: parseFloat(occupancyRate?.toFixed(2))
      };
    });

    return suggestions?.filter(s => s?.action !== 'maintain');
  } catch (error) {
    console.error('Generate dynamic pricing suggestions error:', error?.message);
    throw error;
  }
}

// ==================== CENTER MANAGEMENT OPERATIONS ====================

export async function getCenters(filters = {}) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');
    if (!['admin', 'super_admin']?.includes(userProfile?.role)) throw new Error('Unauthorized: Admin access required');

    let query = supabase?.from('centers')?.select(`
      *,
      manager:user_profiles!centers_manager_id_fkey(id, full_name, email)
    `);

    // Apply filters
    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }
    if (filters?.inventoryType) {
      query = query?.eq('center_type', filters?.inventoryType);
    }
    if (filters?.isActive !== undefined) {
      query = query?.eq('is_active', filters?.isActive);
    }
    if (filters?.search) {
      query = query?.or(`name.ilike.%${filters?.search}%,location.ilike.%${filters?.search}%,city.ilike.%${filters?.search}%`);
    }

    query = query?.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Get centers error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get centers error:', error?.message);
    throw error;
  }
}

export async function getCenterById(centerId) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');
    if (userProfile?.role !== 'admin') throw new Error('Unauthorized: Admin access required');

    const { data, error } = await supabase?.from('centers')?.select(`
      *,
      manager:user_profiles!centers_manager_id_fkey(id, full_name, email)
    `)?.eq('id', centerId)?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Get center error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Get center error:', error?.message);
    throw error;
  }
}

export async function createCenter(centerData) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');
    if (!['admin', 'super_admin']?.includes(userProfile?.role)) throw new Error('Unauthorized: Admin access required');

    const snakeCaseData = convertKeysToSnakeCase(centerData);

    // Ensure center_type is set (required NOT NULL field)
    if (!snakeCaseData?.center_type) {
      snakeCaseData.center_type = snakeCaseData?.inventory_type || 'coworking';
    }
    // Ensure status defaults to active
    if (!snakeCaseData?.status) {
      snakeCaseData.status = 'active';
    }

    const { data, error } = await supabase?.from('centers')?.insert(snakeCaseData)?.select();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Create center error:', error?.message);
      return null;
    }

    const newCenter = data?.[0];

    // Auto-create admin membership for the creator
    if (newCenter?.id) {
      const { error: membershipError } = await supabase?.from('center_memberships')?.insert({
        user_id: userProfile?.id,
        center_id: newCenter?.id,
        role: 'admin',
      });
      if (membershipError) {
        console.warn('Could not create admin membership for center creator:', membershipError?.message);
      }
    }

    // Log activity
    await logActivity({
      module: 'settings',
      action: 'create',
      entityType: 'center',
      entityId: newCenter?.id,
      entityName: newCenter?.name,
      description: `Created center: ${newCenter?.name}`,
    });

    return convertKeysToCamelCase(newCenter);
  } catch (error) {
    console.error('Create center error:', error?.message);
    throw error;
  }
}

export async function updateCenter(centerId, updates) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');
    if (!['admin', 'super_admin']?.includes(userProfile?.role)) throw new Error('Unauthorized: Admin access required');

    const snakeCaseUpdates = convertKeysToSnakeCase(updates);

    // Ensure center_type is set if provided
    if (snakeCaseUpdates?.inventory_type && !snakeCaseUpdates?.center_type) {
      snakeCaseUpdates.center_type = snakeCaseUpdates?.inventory_type;
    }
    // Ensure status is never null
    if (!snakeCaseUpdates?.status) {
      delete snakeCaseUpdates?.status; // Let DB default handle it
    }

    // Non-super_admin cannot change status
    if (userProfile?.role !== 'super_admin') {
      delete snakeCaseUpdates?.status;
    }

    const { data, error } = await supabase?.from('centers')?.update(snakeCaseUpdates)?.eq('id', centerId)?.select();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Update center error:', error?.message);
      return null;
    }

    // Log activity
    await logActivity({
      module: 'settings',
      action: 'update',
      entityType: 'center',
      entityId: centerId,
      entityName: data?.[0]?.name,
      description: `Updated center: ${data?.[0]?.name}`,
    });

    return convertKeysToCamelCase(data?.[0]);
  } catch (error) {
    console.error('Update center error:', error?.message);
    throw error;
  }
}

export async function deleteCenter(centerId) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');
    if (userProfile?.role !== 'admin') throw new Error('Unauthorized: Admin access required');

    // Get center name before deletion
    const center = await getCenterById(centerId);

    const { error } = await supabase?.from('centers')?.delete()?.eq('id', centerId);

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Delete center error:', error?.message);
      return false;
    }

    // Log activity
    await logActivity({
      module: 'settings',
      action: 'delete',
      entityType: 'center',
      entityId: centerId,
      entityName: center?.name,
      description: `Deleted center: ${center?.name}`,
    });

    return true;
  } catch (error) {
    console.error('Delete center error:', error?.message);
    throw error;
  }
}

export async function toggleCenterStatus(centerId, isActive) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');
    if (userProfile?.role !== 'admin') throw new Error('Unauthorized: Admin access required');

    const { data, error } = await supabase?.from('centers')?.update({ is_active: isActive })?.eq('id', centerId)?.select();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Toggle center status error:', error?.message);
      return null;
    }

    // Log activity
    await logActivity({
      module: 'settings',
      action: 'status_change',
      entityType: 'center',
      entityId: centerId,
      entityName: data?.[0]?.name,
      description: `${isActive ? 'Activated' : 'Deactivated'} center: ${data?.[0]?.name}`,
    });

    return convertKeysToCamelCase(data?.[0]);
  } catch (error) {
    console.error('Toggle center status error:', error?.message);
    throw error;
  }
}

export async function assignUserToCenter(centerId, userId, membershipRole) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');
    if (!['admin', 'super_admin']?.includes(userProfile?.role)) throw new Error('Unauthorized: Admin access required');

    // Determine role: use provided role, or derive from user's global role
    let role = membershipRole;
    if (!role) {
      const { data: targetUser } = await supabase?.from('user_profiles')?.select('role')?.eq('id', userId)?.single();
      const userRole = targetUser?.role;
      role = ['admin', 'center_manager', 'sales', 'operations']?.includes(userRole) ? userRole : 'sales';
    }

    const { data, error } = await supabase?.from('center_memberships')?.insert({
      user_id: userId,
      center_id: centerId,
      role,
    })?.select();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Assign user to center error:', error?.message);
      return null;
    }

    // Log activity
    await logActivity({
      module: 'settings',
      action: 'assignment',
      entityType: 'center_membership',
      entityId: data?.[0]?.id,
      description: `Assigned user to center: ${centerId}`,
    });

    return convertKeysToCamelCase(data?.[0]);
  } catch (error) {
    console.error('Assign user to center error:', error?.message);
    throw error;
  }
}

export async function unassignUserFromCenter(centerId, userId) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');
    if (!['admin', 'super_admin']?.includes(userProfile?.role)) throw new Error('Unauthorized: Admin access required');

    const { error } = await supabase?.from('center_memberships')?.delete()?.eq('center_id', centerId)?.eq('user_id', userId);

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Unassign user from center error:', error?.message);
      return false;
    }

    // Log activity
    await logActivity({
      module: 'settings',
      action: 'delete',
      entityType: 'center_membership',
      description: `Unassigned user from center: ${centerId}`,
    });

    return true;
  } catch (error) {
    console.error('Unassign user from center error:', error?.message);
    throw error;
  }
}

export async function getCenterUsers(centerId) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');
    if (!['admin', 'super_admin']?.includes(userProfile?.role)) throw new Error('Unauthorized: Admin access required');

    const { data, error } = await supabase?.from('center_memberships')?.select(`
      *,
      user:user_profiles!center_memberships_user_id_fkey(id, full_name, email, role)
    `)?.eq('center_id', centerId);

    if (error) {
      if (isSchemaError(error)) throw error;
      if (error?.code === 'PGRST116') return [];
      console.log('Get center users error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get center users error:', error?.message);
    throw error;
  }
}

// ==================== USER MANAGEMENT OPERATIONS ====================

export async function getAllUsers(filters = {}) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    // Super admin and admin can view users
    if (!['super_admin', 'admin']?.includes(userProfile?.role)) {
      throw new Error('Only administrators can access user management');
    }

    let query = supabase?.from('user_profiles')?.select(`
      *,
      parent_admin:user_profiles!parent_admin_id(id, full_name, email),
      admin_center_allocations(center_id, centers(name, location))
    `);

    // Super admin sees all, admin sees only their subordinates
    if (userProfile?.role === 'admin') {
      query = query?.or(`parent_admin_id.eq.${userProfile?.id},id.eq.${userProfile?.id}`);
    }

    // Apply filters
    if (filters?.role) {
      query = query?.eq('role', filters?.role);
    }
    if (filters?.isActive !== undefined) {
      query = query?.eq('is_active', filters?.isActive);
    }
    if (filters?.centerId) {
      query = query?.eq('center_id', filters?.centerId);
    }
    if (filters?.search) {
      query = query?.or(`full_name.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%`);
    }

    query = query?.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      if (isSchemaError(error)) throw error;
      if (error?.code === 'PGRST116') return [];
      console.log('Users fetch error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get all users error:', error?.message);
    throw error;
  }
}

export async function createUser(userData) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    // Super admin and admin can create users
    if (!['super_admin', 'admin']?.includes(userProfile?.role)) {
      throw new Error('Only administrators can create users');
    }

    // Create auth user first
    const { data: authData, error: authError } = await supabase?.auth?.signUp({
      email: userData?.email,
      password: userData?.password || 'TempPassword123!',
      options: {
        data: {
          full_name: userData?.fullName,
          role: userData?.role,
          center_id: userData?.centerId,
          phone: userData?.phone
        }
      }
    });

    if (authError) {
      if (isSchemaError(authError)) throw authError;
      throw new Error(authError?.message || 'Failed to create user');
    }

    // Set parent_admin_id if admin is creating user
    if (userProfile?.role === 'admin' && authData?.user?.id) {
      await supabase?.from('user_profiles')
        ?.update({ parent_admin_id: userProfile?.id })
        ?.eq('id', authData?.user?.id);
    }

    // Profile is automatically created by trigger
    return convertKeysToCamelCase(authData?.user);
  } catch (error) {
    console.error('Create user error:', error?.message);
    throw error;
  }
}

export async function updateUser(userId, updates) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    // Super admin and admin can update users
    if (!['super_admin', 'admin']?.includes(userProfile?.role)) {
      throw new Error('Only administrators can update users');
    }

    const dataToUpdate = convertKeysToSnakeCase(updates);

    const { data, error } = await supabase?.from('user_profiles')?.update(dataToUpdate)?.eq('id', userId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Update user error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update user error:', error?.message);
    throw error;
  }
}

export async function deleteUser(userId) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    // Only admins can delete users
    if (userProfile?.role !== 'admin') {
      throw new Error('Only administrators can delete users');
    }

    // Soft delete by deactivating
    const { data, error } = await supabase?.from('user_profiles')?.update({ is_active: false })?.eq('id', userId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Delete user error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete user error:', error?.message);
    throw error;
  }
}

export async function bulkUpdateUserRoles(userIds, newRole) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    // Only admins can perform bulk role updates
    if (userProfile?.role !== 'admin') {
      throw new Error('Only administrators can perform bulk role updates');
    }

    const { data, error } = await supabase?.rpc('bulk_update_user_roles', {
      user_ids: userIds,
      new_role: newRole
    });

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Bulk update roles error:', error?.message);
      return 0;
    }

    return data || 0;
  } catch (error) {
    console.error('Bulk update user roles error:', error?.message);
    throw error;
  }
}

export async function bulkUpdateUserStatus(userIds, isActive) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    // Super admin and admin can perform bulk status updates
    if (!['super_admin', 'admin']?.includes(userProfile?.role)) {
      throw new Error('Only administrators can perform bulk status updates');
    }

    const { data, error } = await supabase?.rpc('bulk_update_user_status', {
      user_ids: userIds,
      new_status: isActive
    });

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Bulk update status error:', error?.message);
      return 0;
    }

    return data || 0;
  } catch (error) {
    console.error('Bulk update user status error:', error?.message);
    throw error;
  }
}

export async function getUserStatistics() {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    // Super admin and admin can view statistics
    if (!['super_admin', 'admin']?.includes(userProfile?.role)) {
      throw new Error('Only administrators can view user statistics');
    }

    const { data, error } = await supabase?.rpc('get_user_statistics');

    if (error && isSchemaError(error)) throw error;

    return convertKeysToCamelCase(data?.[0] || {});
  } catch (error) {
    console.error('Get user statistics error:', error?.message);
    throw error;
  }
}

// Get admin's allocated centers
export async function getAdminCenterAllocations(adminId) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    const { data, error } = await supabase
      ?.from('admin_center_allocations')
      ?.select(`
        *,
        centers(id, name, location, city)
      `)
      ?.eq('admin_id', adminId);

    if (error) {
      if (isSchemaError(error)) throw error;
      if (error?.code === 'PGRST116') return [];
      console.log('Get allocations error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get admin center allocations error:', error?.message);
    throw error;
  }
}

// Allocate centers to admin
export async function allocateCentersToAdmin(adminId, centerIds) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    // Only super admin can allocate centers
    if (userProfile?.role !== 'super_admin') {
      throw new Error('Only super administrators can allocate centers');
    }

    // Delete existing allocations
    await supabase?.from('admin_center_allocations')?.delete()?.eq('admin_id', adminId);

    // Insert new allocations
    const allocations = centerIds?.map(centerId => ({
      admin_id: adminId,
      center_id: centerId,
      allocated_by: userProfile?.id
    }));

    const { data, error } = await supabase
      ?.from('admin_center_allocations')
      ?.insert(convertKeysToSnakeCase(allocations))
      ?.select();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Allocate centers error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Allocate centers to admin error:', error?.message);
    throw error;
  }
}

// Get users under a specific admin (hierarchy)
export async function getUsersByParentAdmin(parentAdminId) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');

    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.eq('parent_admin_id', parentAdminId)
      ?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) throw error;
      if (error?.code === 'PGRST116') return [];
      console.log('Get subordinates error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get users by parent admin error:', error?.message);
    throw error;
  }
}

// ==================== AI CONFIG OPERATIONS ====================

export async function getAIConfig(centerId, configType) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');
    if (userProfile?.role !== 'admin') throw new Error('Unauthorized: Admin access required');

    const { data, error } = await supabase?.from('ai_config')?.select('*')?.eq('center_id', centerId)?.eq('config_type', configType)?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Get AI config error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Get AI config error:', error?.message);
    throw error;
  }
}

export async function updateAIConfig(configId, updates) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');
    if (userProfile?.role !== 'admin') throw new Error('Unauthorized: Admin access required');

    const snakeCaseUpdates = convertKeysToSnakeCase(updates);

    const { data, error } = await supabase?.from('ai_config')?.update(snakeCaseUpdates)?.eq('id', configId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Update AI config error:', error?.message);
      return null;
    }

    await logActivity({
      module: 'settings',
      action: 'update',
      entityType: 'ai_config',
      entityId: configId,
      entityName: 'AI Configuration',
      description: 'Updated AI tuning configuration',
    });

    return convertKeysToCamelCase(data?.[0]);
  } catch (error) {
    console.error('Update AI config error:', error?.message);
    throw error;
  }
}

export async function getAllAIConfigs(centerId) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');
    if (userProfile?.role !== 'admin') throw new Error('Unauthorized: Admin access required');

    let query = supabase?.from('ai_config')?.select('*');
    
    if (centerId) {
      query = query?.eq('center_id', centerId);
    }

    query = query?.order('config_type', { ascending: true });

    const { data, error } = await query;

    if (error) {
      if (isSchemaError(error)) throw error;
      if (error?.code === 'PGRST116') return [];
      console.log('Get all AI configs error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get all AI configs error:', error?.message);
    throw error;
  }
}

// ==================== NOTIFICATION RULES OPERATIONS ====================

export async function getNotificationRules(centerId) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');
    if (userProfile?.role !== 'admin') throw new Error('Unauthorized: Admin access required');

    const { data, error } = await supabase?.from('notification_rules')?.select('*')?.eq('center_id', centerId)?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      if (error?.code === 'PGRST116') return null;
      console.log('Get notification rules error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Get notification rules error:', error?.message);
    throw error;
  }
}

export async function updateNotificationRules(rulesId, updates) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');
    if (userProfile?.role !== 'admin') throw new Error('Unauthorized: Admin access required');

    const snakeCaseUpdates = convertKeysToSnakeCase(updates);

    const { data, error } = await supabase?.from('notification_rules')?.update(snakeCaseUpdates)?.eq('id', rulesId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Update notification rules error:', error?.message);
      return null;
    }

    await logActivity({
      module: 'settings',
      action: 'update',
      entityType: 'notification_rules',
      entityId: rulesId,
      entityName: 'Notification Rules',
      description: 'Updated notification rules configuration',
    });

    return convertKeysToCamelCase(data?.[0]);
  } catch (error) {
    console.error('Update notification rules error:', error?.message);
    throw error;
  }
}

export async function createTestNotification(notificationType, centerId) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');
    if (userProfile?.role !== 'admin') throw new Error('Unauthorized: Admin access required');

    const testNotifications = {
      expiry_alert: {
        title: 'Test: Lock-in Expiry Alert',
        message: 'This is a test notification for lock-in expiry. Unit ABC-101 lock-in expires in 30 days.',
        priority: 'high',
      },
      vacancy_alert: {
        title: 'Test: Vacancy Alert',
        message: 'This is a test notification for vacancy alert. 15 seats are currently vacant.',
        priority: 'medium',
      },
      lead_sla_alert: {
        title: 'Test: Lead SLA Breach',
        message: 'This is a test notification for lead SLA breach. Lead #12345 has not been contacted within 30 minutes.',
        priority: 'critical',
      },
      pricing_suggestion: {
        title: 'Test: Dynamic Pricing Suggestion',
        message: 'This is a test notification for pricing suggestion. Consider increasing price by 10% due to high occupancy.',
        priority: 'low',
      },
      churn_risk: {
        title: 'Test: Churn Risk Alert',
        message: 'This is a test notification for churn risk. Tenant XYZ Corp has high churn probability (85%).',
        priority: 'high',
      },
    };

    const notificationData = testNotifications?.[notificationType] || testNotifications?.expiry_alert;

    const { data, error } = await supabase?.from('notifications')?.insert({
      user_id: userProfile?.id,
      center_id: centerId,
      type: notificationType,
      priority: notificationData?.priority,
      title: notificationData?.title,
      message: notificationData?.message,
      is_test: true,
      entity_type: 'test',
      entity_name: 'Test Notification',
    })?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.log('Create test notification error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create test notification error:', error?.message);
    throw error;
  }
}

// ==================== TEST DATA GENERATOR ====================

export async function generateTestData(centerId) {
  try {
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) throw new Error('User not authenticated');
    if (userProfile?.role !== 'admin') throw new Error('Unauthorized: Admin access required');

    const testTenants = [
      {
        center_id: centerId,
        company_name: 'Test Corp A',
        contact_person: 'John Doe',
        email: 'john@testcorpa.com',
        phone: '+91-9876543210',
        lease_start_date: '2024-01-01',
        lease_end_date: '2025-12-31',
        monthly_rent: 50000,
        security_deposit: 150000,
        status: 'active',
        created_by: userProfile?.id,
      },
      {
        center_id: centerId,
        company_name: 'Test Corp B',
        contact_person: 'Jane Smith',
        email: 'jane@testcorpb.com',
        phone: '+91-9876543211',
        lease_start_date: '2024-06-01',
        lease_end_date: '2026-05-31',
        monthly_rent: 75000,
        security_deposit: 225000,
        status: 'active',
        created_by: userProfile?.id,
      },
      {
        center_id: centerId,
        company_name: 'Test Corp C',
        contact_person: 'Bob Johnson',
        email: 'bob@testcorpc.com',
        phone: '+91-9876543212',
        lease_start_date: '2023-09-01',
        lease_end_date: '2025-08-31',
        monthly_rent: 60000,
        security_deposit: 180000,
        status: 'active',
        created_by: userProfile?.id,
      },
    ];

    const testInventory = [
      {
        center_id: centerId,
        unit_number: 'TEST-101',
        floor: '1',
        area_sqft: 500,
        price_per_sqft: 100,
        total_price: 50000,
        status: 'occupied',
        created_by: userProfile?.id,
      },
      {
        center_id: centerId,
        unit_number: 'TEST-102',
        floor: '1',
        area_sqft: 750,
        price_per_sqft: 100,
        total_price: 75000,
        status: 'occupied',
        created_by: userProfile?.id,
      },
      {
        center_id: centerId,
        unit_number: 'TEST-103',
        floor: '2',
        area_sqft: 600,
        price_per_sqft: 100,
        total_price: 60000,
        status: 'occupied',
        created_by: userProfile?.id,
      },
    ];

    const { data: tenantsData, error: tenantsError } = await supabase?.from('tenants')?.insert(testTenants)?.select();

    if (tenantsError && isSchemaError(tenantsError)) throw tenantsError;

    const { data: inventoryData, error: inventoryError } = await supabase?.from('inventory')?.insert(testInventory)?.select();

    if (inventoryError && isSchemaError(inventoryError)) throw inventoryError;

    await logActivity({
      module: 'settings',
      action: 'create',
      entityType: 'test_data',
      entityName: 'Test Data',
      description: `Generated test data for center ${centerId}`,
    });

    return {
      success: true,
      tenants: tenantsData?.length || 0,
      inventory: inventoryData?.length || 0,
    };
  } catch (error) {
    console.error('Generate test data error:', error?.message);
    throw error;
  }
}

// ==================== FINANCIAL INTELLIGENCE ====================

export async function getCenterFinancials(centerId) {
  try {
    const { data: center, error: centerErr } = await supabase?.from('centers')?.select('id, name, fixed_monthly_operating_cost, target_revenue, total_building_sqft, available_bareshell_sqft')?.eq('id', centerId)?.single();

    if (centerErr) throw centerErr;

    const { data: agreements } = await supabase?.from('agreements')?.select('rent_amount')?.eq('center_id', centerId)?.eq('status', 'active');

    const totalRent = (agreements || [])?.reduce((sum, a) => sum + (parseFloat(a?.rent_amount) || 0), 0);
    const operatingCost = parseFloat(center?.fixed_monthly_operating_cost) || 0;
    const healthScore = operatingCost > 0 ? Math.round((totalRent / operatingCost) * 100) : 0;

    return convertKeysToCamelCase({
      ...center,
      totalRentCollected: totalRent,
      healthScore: healthScore,
    });
  } catch (error) {
    console.error('Get center financials error:', error?.message);
    return null;
  }
}

export async function updateCenterFinancials(centerId, data) {
  try {
    const payload = {};
    if (data?.fixedMonthlyOperatingCost !== undefined) payload.fixed_monthly_operating_cost = data?.fixedMonthlyOperatingCost;
    if (data?.targetRevenue !== undefined) payload.target_revenue = data?.targetRevenue;
    if (data?.totalBuildingSqft !== undefined) payload.total_building_sqft = data?.totalBuildingSqft;
    if (data?.availableBareshellSqft !== undefined) payload.available_bareshell_sqft = data?.availableBareshellSqft;

    const { data: updated, error } = await supabase?.from('centers')?.update(payload)?.eq('id', centerId)?.select()?.single();

    if (error) throw error;
    return convertKeysToCamelCase(updated);
  } catch (error) {
    console.error('Update center financials error:', error?.message);
    throw error;
  }
}

// ==================== COMPLIANCE OPERATIONS ====================

export async function getComplianceRecords(centerId = null) {
  try {
    let query = supabase?.from('center_compliance')?.select('*')?.order('created_at', { ascending: false });
    if (centerId) query = query?.eq('center_id', centerId);
    const { data, error } = await query;
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get compliance records error:', error?.message);
    return [];
  }
}

export async function createComplianceRecord(recordData) {
  try {
    const userProfile = await getCurrentUserProfile();
    const payload = convertKeysToSnakeCase({ ...recordData, createdBy: userProfile?.id });
    const { data, error } = await supabase?.from('center_compliance')?.insert(payload)?.select()?.single();
    if (error) throw error;
    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create compliance record error:', error?.message);
    throw error;
  }
}

export async function updateComplianceRecord(id, updates) {
  try {
    const payload = convertKeysToSnakeCase(updates);
    const { data, error } = await supabase?.from('center_compliance')?.update(payload)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update compliance record error:', error?.message);
    throw error;
  }
}

export async function deleteComplianceRecord(id) {
  try {
    const { error } = await supabase?.from('center_compliance')?.delete()?.eq('id', id);
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Delete compliance record error:', error?.message);
    throw error;
  }
}

// ==================== RISK TAGGING ====================

export function calculateTenantRisk(tenant) {
  if (!tenant) return { risk: 'unknown', label: 'Unknown', color: 'gray' };

  const today = new Date();
  const endDate = tenant?.endDate || tenant?.end_date;
  const lockInMonths = parseInt(tenant?.lockInMonths || tenant?.lock_in_months) || 0;

  if (!endDate) return { risk: 'safe', label: 'Safe', color: 'green' };

  const end = new Date(endDate);
  const diffDays = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
  const lockInDaysLeft = lockInMonths * 30 - Math.ceil((today - new Date(tenant.startDate || tenant.start_date)) / (1000 * 60 * 60 * 24));

  // Safe: Lock-in > 6 months (180 days)
  if (lockInDaysLeft > 180) {
    return { risk: 'safe', label: 'Safe', color: 'green', daysLeft: diffDays };
  }

  // At Risk: Expiry < 60 days AND no renewal initiated
  if (diffDays <= 60 && diffDays > 0) {
    return { risk: 'at_risk', label: 'At Risk', color: 'red', daysLeft: diffDays };
  }

  // Warning: Expiry < 90 days
  if (diffDays <= 90 && diffDays > 0) {
    return { risk: 'warning', label: 'Expiring Soon', color: 'orange', daysLeft: diffDays };
  }

  if (diffDays <= 0) {
    return { risk: 'expired', label: 'Expired', color: 'red', daysLeft: diffDays };
  }

  return { risk: 'safe', label: 'Safe', color: 'green', daysLeft: diffDays };
}

export async function getTenantsWithRiskTags(centerId = null) {
  try {
    let query = supabase?.from('agreements')?.select('*')?.eq('status', 'active');
    if (centerId) query = query?.eq('center_id', centerId);
    const { data, error } = await query;
    if (error) throw error;

    return (data || [])?.map(t => ({
      ...convertKeysToCamelCase(t),
      riskTag: calculateTenantRisk(convertKeysToCamelCase(t)),
    }));
  } catch (error) {
    console.error('Get tenants with risk tags error:', error?.message);
    return [];
  }
}

// ==================== NOTICE PERIOD ====================

export async function setUnitNoticePeriod(unitId, noticePeriodDate) {
  try {
    const { data, error } = await supabase?.from('inventory')?.update({
        notice_period_active: true,
        notice_period_date: noticePeriodDate,
      })?.eq('id', unitId)?.select()?.single();

    if (error) throw error;

    await logActivity({
      module: 'inventory',
      action: 'update',
      entityType: 'unit',
      entityId: unitId,
      entityName: data?.unit_number || unitId,
      description: `Notice period set for unit. Available from ${noticePeriodDate}`,
      newValue: { notice_period_active: true, notice_period_date: noticePeriodDate },
    });

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Set unit notice period error:', error?.message);
    throw error;
  }
}

export async function clearUnitNoticePeriod(unitId) {
  try {
    const { data, error } = await supabase?.from('inventory')?.update({ notice_period_active: false, notice_period_date: null })?.eq('id', unitId)?.select()?.single();

    if (error) throw error;
    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Clear unit notice period error:', error?.message);
    throw error;
  }
}

export { convertKeysToCamelCase };
export { convertKeysToSnakeCase };
function getOperationsDashboardKPIs(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getOperationsDashboardKPIs is not implemented yet.', args);
  return null;
}

export { getOperationsDashboardKPIs };
function getOccupancyHeatmap(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getOccupancyHeatmap is not implemented yet.', args);
  return null;
}

export { getOccupancyHeatmap };
function getActiveUserSessions(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getActiveUserSessions is not implemented yet.', args);
  return null;
}

export { getActiveUserSessions };
function getIncidents(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getIncidents is not implemented yet.', args);
  return null;
}

export { getIncidents };
function subscribeToIncidents(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: subscribeToIncidents is not implemented yet.', args);
  return null;
}

export { subscribeToIncidents };
function subscribeToUserSessions(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: subscribeToUserSessions is not implemented yet.', args);
  return null;
}

export { subscribeToUserSessions };
function calculateRevenueReconciliation(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: calculateRevenueReconciliation is not implemented yet.', args);
  return null;
}

export { calculateRevenueReconciliation };
function getRevenueReconciliation(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getRevenueReconciliation is not implemented yet.', args);
  return null;
}

export { getRevenueReconciliation };
function updatePrimaryRevenueSource(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: updatePrimaryRevenueSource is not implemented yet.', args);
  return null;
}

export { updatePrimaryRevenueSource };